// =============================================================
// Export Ticket to PDF (via browser print dialog)
// Opens a new window with a clean, print-optimised HTML layout
// that the user can "Save as PDF" or print physically.
// =============================================================
import { api, type Ticket } from "./api";
import { PRIORITY_META, STATUS_META, CATEGORY_META } from "./constants";
import { fmtDateTime } from "./format";
import Logo from "../../public/ITDept.jpeg";

/**
 * Build a colour chip for the PDF based on priority.
 */
function priorityColor(priority: string): string {
  const map: Record<string, string> = {
    CRITICAL: "#fb0b14",
    HIGH: "#f97316",
    MEDIUM: "#eab308",
    LOW: "#22c55e",
  };
  return map[priority] || "#94a3b8";
}

function statusColor(status: string): string {
  const map: Record<string, string> = {
    OPEN: "#0ea5e9",
    IN_PROGRESS: "#6366f1",
    ON_HOLD: "#94a3b8",
    RESOLVED: "#22c55e",
    CLOSED: "#64748b",
  };
  return map[status] || "#94a3b8";
}

/**
 * Opens a new browser window containing a professionally-formatted
 * ticket document ready for printing / saving as PDF.
 */
export async function exportTicketPdf(ticket: Ticket) {
  const w = window.open("", "_blank");
  if (!w) {
    alert("Please allow pop-ups to export the ticket as PDF.");
    return;
  }
  w.document.write("<html><head><title>Preparing PDF...</title></head><body style='font-family:sans-serif;display:flex;align-items:center;justify-content:center;height:100vh;margin:0;color:#64748b;background:#f8fafc;'><h2>Preparing PDF Document...</h2></body></html>");

  const priorityLabel = PRIORITY_META[ticket.priority]?.label || ticket.priority;
  const statusLabel = STATUS_META[ticket.status]?.label || ticket.status;
  const categoryLabel = CATEGORY_META[ticket.category]?.label || ticket.category;
  const pColor = priorityColor(ticket.priority);
  const sColor = statusColor(ticket.status);
  const ticketRef = `#${ticket.id.slice(-8).toUpperCase()}`;

  // Always fetch the full ticket to get attachments
  let imageAttachments: any[] = [];
  try {
    const res = await api.get(`/api/tickets/${ticket.id}`);
    const fullTicket = res.data.ticket || res.data;
    imageAttachments = (fullTicket.attachments || []).filter((att: any) => att.mimeType.startsWith("image/"));
  } catch (err) {
    console.warn("Failed to fetch full ticket for attachments", err);
  }

  let imagesHtml = "";

  if (imageAttachments.length > 0) {
    imagesHtml += `
      <div class="section">
        <div class="section-heading">Attached Images (${imageAttachments.length})</div>
        <div style="display: flex; flex-direction: column; gap: 24px;">
    `;

    for (const img of imageAttachments) {
      try {
        const res = await api.get(`/api/attachments/${img.id}`, { responseType: "blob" });
        const blob = new Blob([res.data], { type: img.mimeType });
        const base64 = await new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result as string);
          reader.onerror = reject;
          reader.readAsDataURL(blob);
        });
        
        imagesHtml += `
          <div style="page-break-inside: avoid; margin-bottom: 16px;">
            <div style="font-size: 10px; color: #64748b; margin-bottom: 8px; font-weight: 600;">${escapeHtml(img.filename)}</div>
            <img src="${base64}" style="max-width: 100%; max-height: 500px; border: 1px solid #e2e8f0; border-radius: 8px; object-fit: contain; display: block;" />
          </div>
        `;
      } catch (err) {
        console.error("Failed to load attachment for PDF:", img.filename, err);
        imagesHtml += `
          <div style="page-break-inside: avoid; margin-bottom: 16px;">
            <div style="font-size: 10px; color: #64748b; margin-bottom: 8px; font-weight: 600;">${escapeHtml(img.filename)}</div>
            <div style="padding: 20px; background: #fef2f2; border: 1px solid #fecaca; border-radius: 8px; color: #b91c1c; font-size: 11px;">
              ⚠ Image could not be loaded for PDF export.
            </div>
          </div>
        `;
      }
    }

    imagesHtml += `
        </div>
      </div>
    `;
  }

  const html = `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8" />
  <title>Ticket ${ticketRef} — ${ticket.title}</title>
  <style>
    @page {
      size: A4;
      margin: 18mm 16mm 18mm 16mm;
    }
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body {
      font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
      color: #1e293b;
      background: #fff;
      font-size: 11pt;
      line-height: 1.55;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }

    /* ---- Header ---- */
    .header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      border-bottom: 3px solid #000eb5;
      padding-bottom: 14px;
      margin-bottom: 22px;
    }
    .header-brand {
      display: flex;
      align-items: center;
      gap: 10px;
    }
    .header-logo {
      width: 36px; height: 36px;
      border-radius: 10px;
      object-fit: cover;
    }
    .header-title {
      font-size: 18px;
      font-weight: 800;
      color: #000eb5;
      letter-spacing: -0.5px;
    }
    .header-subtitle {
      font-size: 9px;
      color: #64748b;
      text-transform: uppercase;
      letter-spacing: 1.5px;
      font-weight: 600;
    }
    .header-right {
      text-align: right;
      font-size: 9px;
      color: #64748b;
    }
    .header-right .ref {
      font-size: 13px;
      font-weight: 700;
      color: #0f172a;
      font-family: "SF Mono", "Cascadia Code", Consolas, monospace;
    }

    /* ---- Badge row ---- */
    .badges {
      display: flex;
      gap: 8px;
      flex-wrap: wrap;
      margin-bottom: 18px;
    }
    .badge {
      display: inline-flex;
      align-items: center;
      gap: 5px;
      padding: 4px 12px;
      border-radius: 20px;
      font-size: 9px;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 0.8px;
      border: 1.5px solid;
    }
    .badge-dot {
      width: 7px; height: 7px;
      border-radius: 50%;
    }

    /* ---- Ticket title ---- */
    .ticket-title {
      font-size: 20px;
      font-weight: 700;
      color: #0f172a;
      margin-bottom: 20px;
      line-height: 1.3;
    }

    /* ---- Info Grid ---- */
    .info-grid {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 14px;
      margin-bottom: 22px;
    }
    .info-card {
      background: #f8fafc;
      border: 1px solid #e2e8f0;
      border-radius: 12px;
      padding: 14px 16px;
    }
    .info-card.full {
      grid-column: 1 / -1;
    }
    .info-label {
      font-size: 8px;
      text-transform: uppercase;
      letter-spacing: 1.2px;
      color: #64748b;
      font-weight: 700;
      margin-bottom: 5px;
    }
    .info-value {
      font-size: 12px;
      font-weight: 600;
      color: #0f172a;
    }
    .info-value.small {
      font-size: 11px;
      font-weight: 500;
      color: #334155;
    }

    /* ---- Description ---- */
    .section {
      margin-bottom: 20px;
    }
    .section-heading {
      font-size: 11px;
      text-transform: uppercase;
      letter-spacing: 1px;
      color: #475569;
      font-weight: 700;
      margin-bottom: 8px;
      padding-bottom: 5px;
      border-bottom: 1px solid #e2e8f0;
    }
    .section-body {
      font-size: 11pt;
      color: #334155;
      white-space: pre-wrap;
      line-height: 1.7;
    }

    /* ---- Resolution ---- */
    .resolution-box {
      background: #f0fdf4;
      border: 1.5px solid #bbf7d0;
      border-radius: 12px;
      padding: 14px 16px;
      margin-bottom: 20px;
    }
    .resolution-box .section-heading {
      color: #16a34a;
      border-bottom-color: #bbf7d0;
    }

    /* ---- Footer ---- */
    .footer {
      margin-top: 30px;
      padding-top: 12px;
      border-top: 1px solid #e2e8f0;
      display: flex;
      justify-content: space-between;
      font-size: 8px;
      color: #94a3b8;
      text-transform: uppercase;
      letter-spacing: 1px;
    }

    /* ---- Dates grid ---- */
    .dates-grid {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
      gap: 10px;
      margin-bottom: 20px;
    }

    @media print {
      body { background: #fff; }
    }
  </style>
</head>
<body>

  <!-- Header -->
  <div class="header">
    <div class="header-brand">
      <img src="${window.location.origin}${Logo}" class="header-logo" alt="Logo" />
      <div>
        <div class="header-title">IMPACT Pro</div>
        <div class="header-subtitle">IT Support Ticket Report</div>
      </div>
    </div>
    <div class="header-right">
      <div class="ref">${ticketRef}</div>
      <div>Exported: ${new Date().toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit" })}</div>
    </div>
  </div>

  <!-- Badges -->
  <div class="badges">
    <span class="badge" style="color: ${pColor}; border-color: ${pColor}30; background: ${pColor}10;">
      <span class="badge-dot" style="background: ${pColor};"></span>
      ${priorityLabel}
    </span>
    <span class="badge" style="color: ${sColor}; border-color: ${sColor}30; background: ${sColor}10;">
      <span class="badge-dot" style="background: ${sColor};"></span>
      ${statusLabel}
    </span>
    <span class="badge" style="color: #6366f1; border-color: #6366f130; background: #6366f110;">
      ${categoryLabel}
    </span>
  </div>

  <!-- Title -->
  <div class="ticket-title">${escapeHtml(ticket.title)}</div>

  <!-- Info Grid -->
  <div class="info-grid">
    <div class="info-card">
      <div class="info-label">Submitted By (Agent with Issue)</div>
      <div class="info-value">${escapeHtml(ticket.submitter?.name || "Unknown")}</div>
      ${ticket.submitter?.email ? `<div class="info-value small">${escapeHtml(ticket.submitter.email)}</div>` : ""}
      ${ticket.submitter?.phone_number ? `<div class="info-value small">${escapeHtml(ticket.submitter.phone_number)}</div>` : ""}
      ${ticket.submitter?.department ? `<div class="info-value small">${escapeHtml(ticket.submitter.department)}</div>` : ""}
    </div>
    <div class="info-card">
      <div class="info-label">Assigned To (Agent Resolve)</div>
      <div class="info-value">${ticket.assignee ? escapeHtml(ticket.assignee.name) : '<span style="color:#94a3b8;font-style:italic;">Unassigned</span>'}</div>
      ${ticket.assignee?.email ? `<div class="info-value small">${escapeHtml(ticket.assignee.email)}</div>` : ""}
      ${ticket.assignee?.phone_number ? `<div class="info-value small">${escapeHtml(ticket.assignee.phone_number)}</div>` : ""}
      ${ticket.assignee?.department ? `<div class="info-value small">${escapeHtml(ticket.assignee.department)}</div>` : ""}
    </div>
  </div>

  <!-- Dates -->
  <div class="dates-grid">
    <div class="info-card">
      <div class="info-label">Date Created</div>
      <div class="info-value small">${fmtDateTime(ticket.createdAt)}</div>
    </div>
    <div class="info-card">
      <div class="info-label">Last Updated</div>
      <div class="info-value small">${fmtDateTime(ticket.updatedAt)}</div>
    </div>
    ${ticket.slaDeadline ? `
    <div class="info-card">
      <div class="info-label">SLA Deadline</div>
      <div class="info-value small">${fmtDateTime(ticket.slaDeadline)}</div>
    </div>` : ""}
    ${ticket.resolvedAt ? `
    <div class="info-card">
      <div class="info-label">Date Resolved</div>
      <div class="info-value small">${fmtDateTime(ticket.resolvedAt)}</div>
    </div>` : ""}
  </div>

  <!-- Description -->
  <div class="section">
    <div class="section-heading">Issue Description</div>
    <div class="section-body">${escapeHtml(ticket.description)}</div>
  </div>

  ${imagesHtml}

  <!-- Resolution -->
  ${ticket.resolution ? `
  <div class="resolution-box">
    <div class="section-heading">Resolution Details</div>
    <div class="section-body">${escapeHtml(ticket.resolution)}</div>
  </div>` : ""}

  <!-- Footer -->
  <div class="footer">
    <span>IMPACT Pro — IT Support Ticketing System</span>
    <span>Ticket ${ticketRef} · ${categoryLabel} · ${priorityLabel} Priority</span>
  </div>

  <script>
    window.addEventListener("load", function() {
      var imgs = document.querySelectorAll("img");
      var loaded = 0;
      var total = imgs.length;
      if (total === 0) {
        setTimeout(function() { window.print(); }, 300);
        return;
      }
      function checkPrint() {
        loaded++;
        if (loaded >= total) {
          setTimeout(function() { window.print(); }, 500);
        }
      }
      for (var i = 0; i < total; i++) {
        if (imgs[i].complete) {
          checkPrint();
        } else {
          imgs[i].addEventListener("load", checkPrint);
          imgs[i].addEventListener("error", checkPrint);
        }
      }
    });
  </script>
</body>
</html>`;

  w.document.open();
  w.document.write(html);
  w.document.close();
}

/** Escape HTML entities to prevent XSS in the generated document. */
function escapeHtml(str: string): string {
  return str
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
}
